import { useCallback } from 'react';
import { supabase, handleSupabaseError, getCurrentTimestamp } from '../config/supabase';

export function useCollection(tableName: string) {
  const add = useCallback(async (data: any) => {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .insert({
          ...data,
          created_at: getCurrentTimestamp()
        })
        .select()
        .single();
      
      if (error) throw error;
      return result;
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  const update = useCallback(async (id: string, data: any) => {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .update({
          ...data,
          updated_at: getCurrentTimestamp()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  const remove = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  const getAll = useCallback(async (options: {
    select?: string;
    orderBy?: { column: string; ascending?: boolean };
    limit?: number;
  } = {}) => {
    try {
      let query = supabase.from(tableName).select(options.select || '*');
      
      if (options.orderBy) {
        query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
      }
      
      if (options.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  const getWhere = useCallback(async (
    column: string,
    operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is',
    value: any,
    options: {
      select?: string;
      orderBy?: { column: string; ascending?: boolean };
      limit?: number;
    } = {}
  ) => {
    try {
      let query = supabase
        .from(tableName)
        .select(options.select || '*')
        .filter(column, operator, value);
      
      if (options.orderBy) {
        query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
      }
      
      if (options.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  const getById = useCallback(async (id: string, select?: string) => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select(select || '*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleSupabaseError(error);
    }
  }, [tableName]);

  return {
    add,
    update,
    remove,
    getAll,
    getWhere,
    getById
  };
}